/**
 * FeatureCatalogCard — iter86.3 (Product Ops → Health)
 *
 * Browsable view of the Feature Registry (memory/feature_registry.md —
 * the product's canonical feature catalog). Grouped by area with status,
 * vendors, upstream and surface joins. Read-only: the markdown file is
 * the source of truth (edit via PR; raw file in Docs → FEATURE_REGISTRY).
 */
import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BookOpenCheck, ChevronDown, ChevronRight } from "lucide-react";

const STATUS_PILL = {
    live: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
    planned: "bg-sky-500/15 text-sky-300 border-sky-500/40",
    deprecated: "bg-zinc-800 text-zinc-500 border-zinc-700",
};

const PRODUCT_PILL = {
    tcr: "border-cyber/40 text-cyber",
    "starter-kit": "border-amber-500/40 text-amber-300",
    commons: "border-sky-500/40 text-sky-300",
};

const AREA_ORDER = ["game", "comms", "auth", "billing", "catalog", "platform"];

export default function FeatureCatalogCard({ defaultOpen = false, onJumpToVendor }) {
    const [data, setData] = useState(null);
    const [open, setOpen] = useState(defaultOpen);
    const [productFilter, setProductFilter] = useState("all");
    const ran = useRef(false);

    useEffect(() => {
        if (ran.current) return;
        ran.current = true;
        api.featuresRegistry()
            .then(setData)
            .catch((e) => toast.error(e?.response?.data?.detail || "Feature registry failed to load"));
    }, []);

    const rows = (data?.rows || []).filter(
        (r) => productFilter === "all" || r.product === productFilter
    );
    const grouped = AREA_ORDER
        .map((area) => ({ area, rows: rows.filter((r) => r.area === area) }))
        .filter((g) => g.rows.length > 0);
    const products = ["all", ...Object.keys(data?.counts?.by_product || {})];

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="feature-catalog-card">
            <button
                type="button"
                onClick={() => setOpen(!open)}
                data-testid="feature-catalog-toggle"
                className="w-full flex items-center gap-3 text-left"
            >
                <BookOpenCheck className="w-5 h-5 text-cyber shrink-0" />
                <div className="flex-1 min-w-[200px]">
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                        Feature catalog
                    </h3>
                    <p className="text-[10px] text-zinc-500">
                        Source of truth: <code>feature_registry.md</code> (Docs → FEATURE_REGISTRY).
                        Requirement flow BRD → FRD → TRD via the coverage map (FRD §8, CI-enforced).
                        Canonical ids power the impact radar + vendor tags. Edit via PR, never in-app.
                    </p>
                </div>
                {data && (
                    <span className="text-[10px] text-zinc-400 font-bold" data-testid="feature-catalog-counts">
                        {data.counts.total} features · {data.counts.by_status.live || 0} live · {data.counts.by_status.planned || 0} planned
                    </span>
                )}
                {open ? <ChevronDown className="w-4 h-4 text-zinc-500" /> : <ChevronRight className="w-4 h-4 text-zinc-500" />}
            </button>

            {open && (
                <div className="flex flex-wrap items-center gap-1" data-testid="feature-catalog-product-filter">
                    <span className="text-[9px] uppercase tracking-wider text-zinc-600 font-bold mr-1">Product home:</span>
                    {products.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setProductFilter(p)}
                            data-testid={`feature-catalog-filter-${p}`}
                            className={`pill text-[9px] font-bold uppercase border transition-colors ${productFilter === p ? "border-white text-white bg-white/10" : (PRODUCT_PILL[p] || "border-zinc-700 text-zinc-500")} hover:text-white`}
                        >
                            {p === "all" ? `all (${data?.counts?.total || 0})` : `${p} (${data?.counts?.by_product?.[p] || 0})`}
                        </button>
                    ))}
                </div>
            )}

            {open && grouped.map(({ area, rows: areaRows }) => (
                <div key={area} data-testid={`feature-catalog-area-${area}`}>
                    <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 mb-1.5">
                        <span className="text-cyber">{area}</span> · {areaRows.length}
                    </p>
                    <div className="space-y-1">
                        {areaRows.map((r) => (
                            <div
                                key={r.feature_id}
                                className="flex items-center gap-2 flex-wrap border border-zinc-800 rounded p-2 text-[11px] bg-black/30"
                                data-testid={`feature-catalog-row-${r.feature_id.replace(/\./g, "-")}`}
                            >
                                <code className="font-bold text-white">{r.feature_id}</code>
                                <span className={`pill text-[9px] font-bold uppercase border ${STATUS_PILL[r.status] || STATUS_PILL.planned}`}>
                                    {r.status}
                                </span>
                                {r.product && r.product !== "tcr" && (
                                    <span className={`pill text-[9px] font-bold uppercase border ${PRODUCT_PILL[r.product] || "border-zinc-700 text-zinc-500"}`} data-testid={`feature-catalog-product-${r.feature_id.replace(/\./g, "-")}`}>
                                        {r.product}
                                    </span>
                                )}
                                <span className="text-zinc-400">{r.label}</span>
                                {r.docs && (
                                    <span className="pill text-[9px] border-zinc-700 text-zinc-400" title="Requirement homes: BRD → FRD → TRD (coverage map, FRD §8)" data-testid={`feature-catalog-docs-${r.feature_id.replace(/\./g, "-")}`}>
                                        {["BRD", "FRD", "TRD"].map((d) => `${d} ${r.docs[d] || "—"}`).join(" → ")}
                                    </span>
                                )}
                                <span className="ml-auto text-[9px] text-zinc-600 text-right">
                                    {r.vendors.map((vn, i) => (
                                        <span key={vn}>
                                            {i > 0 && ", "}
                                            {onJumpToVendor && vn !== "internal" ? (
                                                <button
                                                    type="button"
                                                    onClick={() => onJumpToVendor(vn)}
                                                    title={`Jump to ${vn} in the Vendor Registry`}
                                                    data-testid={`feature-vendor-link-${r.feature_id.replace(/\./g, "-")}-${vn.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                                    className="underline decoration-dotted underline-offset-2 hover:text-cyber transition-colors"
                                                >
                                                    {vn}
                                                </button>
                                            ) : vn}
                                        </span>
                                    ))}
                                    {r.upstream ? ` · ${r.upstream}` : ""}
                                    {r.surfaces && r.surfaces !== "—" ? ` · ${r.surfaces}` : ""}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}
